import { useCallback, useEffect, useState } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * The closed vocabularies, mirrored from the API.
 *
 * They are closed on the server for a reason worth repeating here: asked for a
 * material in free text the model produced 91 distinct answers across 148
 * products. Only an enumerated field can be filtered on, so these lists are the
 * filters — and because they are small and fixed, the catalogue can also read a
 * typed query against them without asking a model anything.
 */
export const COLOR_FAMILIES = [
    'white', 'beige', 'brown', 'grey', 'black',
    'blue', 'green', 'red', 'orange', 'yellow', 'pink', 'purple',
    'metallic', 'multicolour',
] as const;

export const MATERIAL_FAMILIES = [
    'ceramic', 'porcelain', 'stone', 'marble', 'terrazzo', 'concrete',
    'wood', 'laminate', 'metal', 'glass', 'textile', 'leather', 'rattan',
    'composite', 'other',
] as const;

export const STYLES = [
    'minimal', 'contemporary', 'classic', 'traditional', 'rustic',
    'industrial', 'mid-century', 'scandinavian', 'coastal', 'bohemian',
    'art-deco', 'glam',
] as const;

export const FINISHES = [
    'matte', 'satin', 'polished', 'glossy', 'textured',
    'brushed', 'honed', 'distressed', 'ribbed', 'metallic',
] as const;

export const COLOR_TEMPERATURES = ['warm', 'neutral', 'cool'] as const;

/** What a product looks like, as the vision pass described it. */
export interface ProductAppearance {
    description: string;
    material?: string;
    materialFamily?: string;
    finish?: string;
    style?: string[];
    colorTemperature?: string;
    dominantColors?: string[];
    colorFamily?: string[];
    suitedEnvironments?: string[];
    avoidEnvironments?: string[];
}

/** Keyed by product uuid. A product with no analysis is simply absent. */
export type AppearanceMap = Record<string, ProductAppearance>;

const BASE = '/services/pms/content-studio/product-insights';

/**
 * Every analysed product's appearance, fetched once.
 *
 * The catalogue filters client-side over the whole list, so one read of
 * everything beats a query per filter change — and it means colour and style
 * can be combined with the existing filters without a round trip.
 */
export const useProductInsights = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const [map, setMap] = useState<AppearanceMap>({});
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await sendAuthGuardedRequest(ApiMethod.GET, BASE);
            setMap((res ?? {}) as AppearanceMap);
        } catch {
            // A catalogue that cannot reach appearances is still a catalogue.
            // The filters hide themselves rather than the page failing.
            setMap({});
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { appearances: map, loading, reload: load };
};

export interface CatalogSearchResult {
    uuids: string[];
    understood: string;
    unavailable?: string;
}

/**
 * Ask a model which products match a phrase.
 *
 * Costs a credit or two per call, so the caller runs it on Enter rather than
 * as the vendor types — which is also why it is a plain function and not a
 * hook that fires on a value changing.
 */
export const searchCatalogWithAi = async (
    send: (method: ApiMethod, url: string, body?: unknown) => Promise<unknown>,
    query: string,
): Promise<CatalogSearchResult> => {
    const res = (await send(ApiMethod.POST, `${BASE}/search`, { query })) as CatalogSearchResult | null;
    return { uuids: res?.uuids ?? [], understood: res?.understood ?? '', unavailable: res?.unavailable };
};

export interface AppearanceFilters {
    colorFamily: string[];
    materialFamily: string[];
    style: string[];
    finish: string[];
}

export const EMPTY_APPEARANCE_FILTERS: AppearanceFilters = {
    colorFamily: [],
    materialFamily: [],
    style: [],
    finish: [],
};

/** True when nothing in this group is being filtered on. */
export const noAppearanceFilter = (f: AppearanceFilters): boolean =>
    !f.colorFamily.length && !f.materialFamily.length && !f.style.length && !f.finish.length;

/**
 * Does one product's appearance satisfy the chosen filters?
 *
 * Within a group the values are OR'd — "green or blue" is one question. Across
 * groups they are AND'd, because "green" and "wood" asked together is plainly a
 * narrowing, not a widening.
 */
export function matchesAppearance(a: ProductAppearance | undefined, f: AppearanceFilters): boolean {
    if (noAppearanceFilter(f)) return true;
    // Unanalysed products drop out of an appearance filter rather than being
    // kept: claiming a product is green when nothing ever looked at it is the
    // one answer a filter must not give.
    if (!a) return false;

    const some = (chosen: string[], has: string[] | undefined) =>
        !chosen.length || (has ?? []).some((v) => chosen.includes(v));
    const one = (chosen: string[], has: string | undefined) =>
        !chosen.length || (has ? chosen.includes(has) : false);

    return (
        some(f.colorFamily, a.colorFamily) &&
        one(f.materialFamily, a.materialFamily) &&
        some(f.style, a.style) &&
        one(f.finish, a.finish)
    );
}

/**
 * Read a typed phrase as appearance filters — "show green wood products".
 *
 * No model call. The vocabularies are closed and total about fifty words, so a
 * scan over them answers the queries people actually type, instantly, offline
 * and for nothing. A model would add a second of latency and a credit charge to
 * a search box, which is the wrong trade for matching a colour name.
 *
 * Returns the words it consumed as well, so the caller can pass the rest to the
 * ordinary name/SKU search rather than searching for "show" and finding nothing.
 */
export function readQuery(query: string): { filters: AppearanceFilters; rest: string } {
    const filters: AppearanceFilters = {
        colorFamily: [], materialFamily: [], style: [], finish: [],
    };
    if (!query.trim()) return { filters, rest: '' };

    const groups: Array<[keyof AppearanceFilters, readonly string[]]> = [
        ['colorFamily', COLOR_FAMILIES],
        ['materialFamily', MATERIAL_FAMILIES],
        ['style', STYLES],
        ['finish', FINISHES],
    ];

    const leftover: string[] = [];
    for (const word of query.split(/\s+/).filter(Boolean)) {
        // Singularised and stripped, so "greens" and "tiles," still land.
        const clean = word.toLowerCase().replace(/[^a-z-]/g, '').replace(/s$/, '');
        let used = false;
        for (const [key, vocabulary] of groups) {
            const hit = vocabulary.find((v) => v === clean || v === `${clean}s` || v.replace('-', '') === clean);
            if (hit && !filters[key].includes(hit)) {
                filters[key].push(hit);
                used = true;
                break;
            }
        }
        // Filler that only ever surrounds a real term. Dropped rather than
        // searched for, or "show me green" finds nothing named "show".
        if (!used && !/^(show|me|all|find|the|a|an|with|in|of|products?|product)$/.test(clean)) {
            leftover.push(word);
        }
    }

    return { filters, rest: leftover.join(' ') };
}
